/**
 * Noibu Feature Flag: compact desktop header for new visitors
 * (flag "2083-compact-desktop-header-for-new-visitors", Noibu A/B test 242).
 *
 * Structurally parallel to assets/noibu-flag-banners.js. It is a separate file
 * on purpose: the other flag files define controls for running experiments and
 * must not change mid-flight.
 *
 * Both arms are server-rendered by Liquid. This script builds no header DOM; it
 * sets a class on <html> that decides whether the compact layout applies (see
 * sections/noibu-compact-header-ab.liquid and noibu-compact-header-line.liquid).
 * A flag failure is therefore a no-op and the page renders exactly as today.
 *
 *   original        -> html.nff-compact-header--a  (control, the default)
 *   compact-header  -> html.nff-compact-header--b
 *
 * WHO ASKS THE FLAG
 * Targeting (device type "other" = desktop) lives in the Noibu test. Noibu
 * cannot target new vs returning visitors, so the gate section stamps a
 * cohort on <html data-nff-ch-cohort> once per browser (see its comment).
 * Only the "new" cohort calls the SDK. "returning" never evaluates the flag,
 * renders the control, and reports nff_compact-header=returning so results
 * can be cut by cohort if Noibu enrols sessions on targeting alone.
 * There is no CSS breakpoint on enrolment: the flag decides who sees B.
 *
 * Noibu's implementation code for test 242 (console > A/B tests > 242 >
 * Implementation), implemented faithfully below in render():
 *
 *   function renderCompactHeader(container) {
 *     // TODO: the implementation for variation B
 *   }
 *
 *   function renderOriginal(container) {
 *     // TODO: the implementation for the default experience
 *   }
 *
 *   function render(container) {
 *     const { variant } = client.getStringDetails(
 *       "2083-compact-desktop-header-for-new-visitors",
 *       "original"
 *     );
 *
 *     switch (variant) {
 *       case "compact-header":
 *         // Variation B
 *         return renderCompactHeader(container);
 *       case "original":
 *         // Variation A (control)
 *         // Since this is the original experience, add no code here and
 *         // let this fall through to the default.
 *       default:
 *         // This should always be the original experience. Users excluded
 *         // by targeting will see this.
 *         return renderOriginal(container);
 *     }
 *   }
 *
 *   // `client` is the ready client from the Noibu A/B testing SDK.
 *   // Call render(container) once your feature flag client is ready.
 *
 * The SDK's details object carries the arm in `variant` (and `value`); older
 * generated snippets destructured `variation`, which always fell through to the
 * control. readVariation() reads all three so neither shape can silently send
 * every B visitor the control.
 *
 * DEPLOY BEFORE STARTING THE TEST
 * While the test is a draft the SDK serves the default, so every visitor gets
 * the control and this file is inert. Shoppers bucketed into B before this code
 * is live are counted in B while seeing A, and that data cannot be repaired.
 */
(function () {
  'use strict';

  var FLAG = '2083-compact-desktop-header-for-new-visitors';
  var CONTROL = 'original';
  var VARIATION_B = 'compact-header';
  var CACHE_KEY = 'nff.compact-header';

  var REPORT_KEY = 'nff_compact-header';
  var STATE_ATTR = 'data-nff-compact-header-state';

  // Anchored to window.load because the client cannot arrive before it:
  // collect-core is injected by a ScriptTag on load, and collect-ff by
  // collect-core after that.
  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-compact-header--a';
  CLASSES[VARIATION_B] = 'nff-compact-header--b';

  var root = document.documentElement;

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }

    // NOIBUJS.addCustomAttribute comes from collect-core, which loads
    // independently of collect-ff, so reporting works even when flags do not.
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // collect-core may still be booting. Retry rather than drop the exposure.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load. Is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function syncHeader() {
    // Dawn caches --header-height and the sticky header's bounds when the
    // header connects. B changes the header's height, so refresh both once the
    // arm is applied, or the predictive search, the dropdowns and the sticky
    // reveal work from the old offsets.
    var header = document.querySelector('.section-header');
    if (header) {
      root.style.setProperty('--header-height', header.offsetHeight + 'px');
    }
    var sticky = document.querySelector('sticky-header');
    if (sticky && typeof sticky.createObserver === 'function') {
      try {
        sticky.createObserver();
      } catch (error) {
        /* no-op: older Dawn builds without createObserver */
      }
    }
  }

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (root.classList.contains(className)) {
      if (typeof window.nffCompactHeaderLayout === 'function') window.nffCompactHeaderLayout();
      return;
    }

    // Keep the shopper's place. Switching to B after first paint removes about
    // 340px above <main> on desktop. At the very top of the page that is the
    // point of the test, but once the shopper has scrolled, the same collapse
    // would yank what they are reading upward, so scroll back by the
    // difference to hold it still.
    var main = document.getElementById('MainContent');
    var before = main && window.pageYOffset > 0 ? main.getBoundingClientRect().top : null;

    root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
    root.classList.add(className);
    root.setAttribute('data-nff-compact-header', variation);

    if (typeof window.nffCompactHeaderLayout === 'function') window.nffCompactHeaderLayout();

    if (before !== null) {
      var shift = main.getBoundingClientRect().top - before;
      if (shift) window.scrollBy(0, shift);
    }

    syncHeader();

    document.dispatchEvent(
      new CustomEvent('noibu:compact-header-variation', { detail: { variation: variation } })
    );
  }

  function remember(variation) {
    // Cached (7 days) so a returning B visitor does not watch the header
    // collapse on every page. The SDK stays the source of truth; this only
    // feeds the paint-time hint in the gate section. Wrapped because storage
    // throws in private mode and some embedded webviews.
    try {
      window.localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ v: variation, t: Date.now() })
      );
    } catch (error) {
      /* no-op */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_compact_header=compact-header or =original
    try {
      var value = new URLSearchParams(window.location.search).get('nff_compact_header');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable: fall through to the flag. */
    }
    return null;
  }

  function readVariation(client) {
    if (typeof client.getStringDetails === 'function') {
      var details = client.getStringDetails(FLAG, CONTROL) || {};
      return details.variant || details.variation || details.value || CONTROL;
    }
    // Older SDK builds without getStringDetails.
    return client.getStringValue(FLAG, CONTROL);
  }

  function renderCompactHeader() {
    activate(VARIATION_B);
    remember(VARIATION_B);
    return VARIATION_B;
  }

  function renderOriginal() {
    activate(CONTROL);
    remember(CONTROL);
    return CONTROL;
  }

  function render(client) {
    var variant = readVariation(client);

    switch (variant) {
      case VARIATION_B:
        // Variation B
        return renderCompactHeader();
      case CONTROL:
      // Variation A (control): falls through to the default.
      default:
        // Control, visitors excluded by targeting (mobile), and unresolved
        // flags all land here.
        return renderOriginal();
    }
  }

  var forced = forcedVariation();
  if (forced) {
    // Skips remember() and the SDK so a forced preview neither poisons the
    // visitor's cache nor records an exposure.
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  if (root.getAttribute('data-nff-ch-cohort') !== 'new') {
    // Returning visitors are outside the test: control, no flag evaluation.
    activate(CONTROL);
    reportState('returning');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));

      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      // Do NOT remember() here: caching a value the SDK never produced would
      // replay a decision that was never made. A new visitor keeps the arm
      // replayed from their earlier, real assignment so the header does not
      // jump back mid-visit.
      if (!root.hasAttribute('data-nff-compact-header')) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
